// HerkunftAbschnitt.tsx — „Wie kommt der Kontakt zustande?" in der Anlage
// (Firma und Person), Refined statt Fluent (PO 23.09.2026).
//
// Dieselben Chips wie in der Akte (`OptionChips`), darunter je gewählter
// Herkunft mit Bezug das Kontakt-Suchfeld und die Frage, ob der gewählte
// Kontakt als Empfehlungsgeber bzw. Tippgeber geführt werden soll. Gespeichert
// wird erst nach dem Anlegen (`herkunftVerknuepfen`).
//
// T-324 (15.10.2026): Ist der Empfehlungsgeber noch nicht im System, wird er
// hier eingetippt (`ContactRefField`, wie beim Arbeitgeber) und beim Speichern
// angelegt. Die Suche bleibt das `KontaktSuchfeld` — nur das Eintippen kommt
// aus `ContactRefField`.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { FieldRow, LabeledInput } from "../forms/formPrimitives";
import { OptionChips } from "../forms/widgets/OptionChips";
import { KontaktSuchfeld, type KontaktTreffer } from "../forms/widgets/KontaktSuchfeld";
import { apiUrl } from "../../utils/api";
import { KontaktName } from "../forms/KontaktSprung";
import { ContactRefField } from "../relationsInline/ContactRefField";
import { useRelStyles } from "../relationsInline/styles";
import { refDisplayName, type ContactRef } from "../relationsInline/types";
import { herkunftBestandLesen } from "./herkunftBezug";
import {
  HERKUNFT_ANLAGE_OPTIONEN,
  HERKUNFT_SONSTIGES,
  herkunftDetailsLesen,
  herkunftZerlegen,
  herkunftZusammensetzen,
  type HerkunftDetail,
} from "../../utils/kontaktherkunft";
import { HERKUNFT_ARTEN, HERKUNFT_BEZUG, bezugLesen, bezugSchreiben, hatStatus, type HerkunftArt, type HerkunftBezug } from "./herkunftBezug";

const useStyles = makeStyles({
  abschnitt: { display: "grid", rowGap: "18px" },
  bezug: { display: "grid", rowGap: "10px" },
  bestand: {
    display: "grid", rowGap: "6px", listStyleType: "none",
    ...shorthands.margin("0"), ...shorthands.padding("10px", "12px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"), backgroundColor: REFINED.aub50,
    fontFamily: REFINED.fontUI, fontSize: "0.9rem", color: REFINED.ink,
  },
  check: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  checkbox: { accentColor: REFINED.aub, width: "16px", height: "16px", ...shorthands.margin("0") },
  hinweis: { fontFamily: REFINED.fontUI, fontSize: "0.85rem", color: REFINED.ink2 },
  fehler: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", color: REFINED.err },
  sonstiges: { width: "min(100%, 500px)" },
  option: { display: "grid", rowGap: "10px" },
  optionTitel: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: REFINED.ink3,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
});

/** Formularfeld mit `{ "<Herkunft>": { wann, wie } }` als JSON-Text. */
export const HERKUNFT_DETAILS_FELD = "kontaktherkunftDetails";

/** Je gewählter Herkunft: Wann | Wie (User 27.09.2026). */
function WannWie({
  herkunft,
  wert,
  onChange,
}: {
  herkunft: string;
  wert: HerkunftDetail;
  onChange: (d: HerkunftDetail) => void;
}) {
  return (
    <FieldRow columns={2}>
      <LabeledInput
        label="Wann ist der Kontakt zustande gekommen?"
        name={`herkunft-wann-${herkunft}`}
        type="date"
        value={wert.wann ?? ""}
        onChange={(v) => onChange({ ...wert, wann: v })}
      />
      <LabeledInput
        label="Wie ist der Kontakt zustande gekommen?"
        name={`herkunft-wie-${herkunft}`}
        value={wert.wie ?? ""}
        onChange={(v) => onChange({ ...wert, wie: v })}
        placeholder="z. B. Messe, Vortrag, Anruf"
        maxLength={500}
      />
    </FieldRow>
  );
}

/**
 * Die Vollanlage („In der vollständigen Kontaktanlage weiter erfassen") legt
 * den eingetippten Kontakt sofort an — ab da ist er ein gewöhnlicher Treffer.
 */
function trefferAusRef(r: ContactRef & { contactId: number }): KontaktTreffer {
  return {
    contactId: r.contactId,
    displayName: refDisplayName(r),
    contactType: r.type,
    art: r.type === "Firma" ? "firma" : "privat",
    statusName: r.status ?? null,
    companyId: null,
    companyName: null,
    defaultEmail: null,
    contactNbrInt: null,
  };
}

/** Ein gewählter Kontakt plus die Frage nach dem Status. */
function BezugFeld({
  art,
  wert,
  bestandRoh,
  onChange,
}: {
  art: HerkunftArt;
  bestandRoh: unknown;
  wert: HerkunftBezug | null;
  onChange: (b: HerkunftBezug | null) => void;
}) {
  const s = useStyles();
  const rel = useRelStyles();
  const cfg = HERKUNFT_BEZUG[art];
  const bestand = React.useMemo(() => herkunftBestandLesen(bestandRoh), [bestandRoh]);
  const ausblendenIds = React.useMemo(() => bestand.map((p) => p.contactId), [bestand]);
  // null = noch unbekannt (lädt), sonst: hat der Kontakt den Status schon?
  const [bereits, setBereits] = React.useState<boolean | null>(null);
  const wertRef = React.useRef(wert);
  wertRef.current = wert;
  const id = wert?.treffer?.contactId;

  React.useEffect(() => {
    setBereits(null);
    if (!id) return;
    let abgebrochen = false;
    (async () => {
      let extra: string[] = [];
      try {
        const res = await fetch(apiUrl(`contact/${id}`));
        if (res.ok) extra = ((await res.json()) as { extraStatuses?: string[] })?.extraStatuses ?? [];
      } catch {
        /* ohne Antwort bleibt die Frage offen — lieber fragen als raten */
      }
      if (abgebrochen) return;
      const hat = hatStatus(wertRef.current?.treffer?.statusName, extra, cfg.status);
      setBereits(hat);
      // Wer den Status schon hat, braucht ihn nicht noch einmal — und ein
      // zweiter Aufruf scheiterte womöglich nur am fehlenden Schreibrecht.
      if (hat && wertRef.current?.alsStatus) onChange({ ...wertRef.current, alsStatus: false });
    })();
    return () => {
      abgebrochen = true;
    };
    // onChange wechselt je Render; maßgeblich ist nur der gewählte Kontakt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, cfg.status]);

  return (
    <div className={s.bezug}>
      {bestand.length > 0 && (
        <>
          <span className={s.hinweis}>Zugeordnete {cfg.suchLabel}</span>
          <ul className={s.bestand} aria-label={`Zugeordnete ${cfg.suchLabel}`}>
            {bestand.map((p) => (
              <li key={p.contactId}><KontaktName contactId={p.contactId}>{p.displayName}</KontaktName></li>
            ))}
          </ul>
        </>
      )}
      {wert?.neu ? (
        <>
          <span className={s.optionTitel}>Neuer {cfg.suchLabel}</span>
          <span className={s.hinweis}>
            Ist noch nicht im System und wird beim Speichern angelegt — als „{cfg.status}“ und mit der
            Verantwortung (FO/BO) dieses Kontakts.
          </span>
          <ContactRefField
            wantType="Person"
            allowTypeSwitch
            firmaExtraFields
            idBase={`herkunft-${art}-neu`}
            value={wert.neu}
            onChange={(r) => {
              if (!r) onChange(null);
              else if (r.contactId) onChange({ treffer: trefferAusRef(r as ContactRef & { contactId: number }), alsStatus: true });
              else onChange({ neu: r, alsStatus: true });
            }}
          />
        </>
      ) : (
        <>
          <KontaktSuchfeld
            label={bestand.length ? `Weiteren ${cfg.suchLabel} zuordnen` : cfg.suchLabel}
            ausblendenIds={ausblendenIds}
            name={`herkunft-${art}`}
            placeholder="Kontakt suchen (Name, Firma, E-Mail) …"
            value={wert?.treffer ?? null}
            onChange={(t: KontaktTreffer | null) => onChange(t ? { treffer: t, alsStatus: true } : null)}
          />
          {!wert && (
            <button
              type="button"
              className={rel.newLink}
              onClick={() => onChange({ neu: { type: "Person", manual: true }, alsStatus: true })}
            >
              + {cfg.suchLabel} ist noch nicht im System — neu anlegen
            </button>
          )}
        </>
      )}
      {wert?.treffer && bereits === true && (
        <span className={s.hinweis}>
          {wert.treffer.displayName} ist bereits als {cfg.status} geführt.
        </span>
      )}
      {wert?.treffer && bereits === false && (
        <label className={s.check}>
          <input
            type="checkbox"
            className={s.checkbox}
            checked={wert.alsStatus}
            onChange={(e) => onChange({ ...wert, alsStatus: e.target.checked })}
          />
          {wert.treffer.displayName} {cfg.statusText}
        </label>
      )}
    </div>
  );
}

export function HerkunftAbschnitt({
  isInteressent,
  auswahl,
  toggle,
  sonstiges,
  onSonstiges,
  formFields,
  set,
}: {
  isInteressent: boolean;
  /** Gewählte Herkünfte (aus `useContactOrigins`). */
  auswahl: string[];
  toggle: (option: string, checked: boolean) => void;
  sonstiges: string;
  onSonstiges: (v: string) => void;
  formFields: Record<string, unknown>;
  set: (key: string, value: string) => void;
}) {
  const s = useStyles();

  // Die Chips liefern die ganze neue Auswahl; die Regeln (Eigenakquise schließt
  // aus, Abwählen leert die Folgefelder) stehen im Hook — also Einzelschritte.
  const chipsAendern = (v: string) => {
    const neu = herkunftZerlegen(v);
    for (const o of neu.filter((x) => !auswahl.includes(x))) toggle(o, true);
    for (const o of auswahl.filter((x) => !neu.includes(x))) toggle(o, false);
  };

  const details = herkunftDetailsLesen(formFields[HERKUNFT_DETAILS_FELD]);
  const setzeDetail = (herkunft: string, d: HerkunftDetail) =>
    set(HERKUNFT_DETAILS_FELD, JSON.stringify({ ...details, [herkunft]: d }));
  const titel = (h: string) => HERKUNFT_ANLAGE_OPTIONEN.find((o) => o.value === h)?.label ?? h;
  // Reihenfolge wie die Chips, nicht wie angeklickt.
  const gewaehlt = HERKUNFT_ANLAGE_OPTIONEN.map((o) => o.value).filter((v) => auswahl.includes(v));

  return (
    <div className={s.abschnitt}>
      <OptionChips
        label="Herkunft"
        name="kontaktherkunft-anlage"
        options={HERKUNFT_ANLAGE_OPTIONEN}
        value={herkunftZusammensetzen(auswahl)}
        onChange={chipsAendern}
      />
      {isInteressent && auswahl.length === 0 && (
        <span className={s.fehler}>Bitte mindestens eine Herkunft auswählen.</span>
      )}
      {gewaehlt.map((h) => (
        <div className={s.option} key={h}>
          {gewaehlt.length > 1 && <span className={s.optionTitel}>{titel(h)}</span>}
          {(HERKUNFT_ARTEN as readonly string[]).includes(h) && (
            <BezugFeld
              art={h as HerkunftArt}
              bestandRoh={formFields[`${HERKUNFT_BEZUG[h as HerkunftArt].feld}Bestand`]}
              wert={bezugLesen(formFields[HERKUNFT_BEZUG[h as HerkunftArt].feld])}
              onChange={(b) => set(HERKUNFT_BEZUG[h as HerkunftArt].feld, bezugSchreiben(b))}
            />
          )}
          {h === HERKUNFT_SONSTIGES && (
            <div className={s.sonstiges}>
              <LabeledInput
                label="Sonstiges"
                name="contactOriginOther"
                value={sonstiges}
                onChange={onSonstiges}
                placeholder="Bitte angeben, z. B. Messe, Vortrag"
              />
            </div>
          )}
          <WannWie herkunft={h} wert={details[h] ?? {}} onChange={(d) => setzeDetail(h, d)} />
        </div>
      ))}
    </div>
  );
}
